/**
 * Demand → ResourceRequest adapter
 *
 * Converts backend Demand / DemandLine models into the frontend
 * ResourceRequest shape consumed by ResourceRequestsTab and the
 * planning engine.
 */

import type { Demand, DemandLine } from '@/types/demand';
import type {
  RequestedSkill,
  RequestPriority,
  ResourceRequest,
  ResourceRequestStatus,
  SkillRequirement,
} from '@/types/resourceRequest';

// ── Status mapping ────────────────────────────────────────────────────────────

const STATUS_MAP: Record<string, ResourceRequestStatus> = {
  'open':             'pending',
  'partially-filled': 'pending',
  'filled':           'accepted',
  'cancelled':        'declined',
};

/**
 * Backend status, refined by what has actually been booked: a demand with some
 * hours allocated but not all of them is 'partially-allocated' regardless of the
 * status string the source system sends.
 */
function mapStatus(demand: Demand): ResourceRequestStatus {
  const base = STATUS_MAP[demand.status] ?? 'pending';
  if (base === 'declined') return base;
  const allocated = Math.max(0, demand.allocatedHours ?? 0);
  if (allocated <= 0) return base;
  return demandRemainingHours(demand) > 0 ? 'partially-allocated' : 'accepted';
}

// ── Priority mapping ──────────────────────────────────────────────────────────

const PRIORITY_MAP: Record<string, RequestPriority> = {
  critical: 'critical',
  high:     'high',
  medium:   'medium',
  low:      'low',
};

function mapPriority(demandPriority: string): RequestPriority {
  return PRIORITY_MAP[demandPriority.toLowerCase()] ?? 'medium';
}

// ── DemandLines → monthlyHourHints ────────────────────────────────────────────

/** Hours still to allocate on a demand line, never negative. */
function lineRemainingHours(line: DemandLine): number {
  const remaining = typeof line.remainingHours === 'number'
    ? line.remainingHours
    : line.budgetHours - (line.allocatedHours ?? 0);
  return Math.max(0, remaining);
}

/**
 * Per-month hints covering only the hours left to allocate. Fully allocated
 * months are omitted so a partially-filled demand is never re-planned into
 * months that are already booked.
 */
function buildMonthlyHourHints(lines: DemandLine[]): Record<string, number> {
  const hints: Record<string, number> = {};
  for (const line of lines) {
    // Use the 'month' field (e.g. "2026-06") as the key
    if (!line.month) continue;
    const remaining = lineRemainingHours(line);
    if (remaining <= 0) continue;
    hints[line.month] = remaining;
  }
  return hints;
}

/** Hours already booked per month, for the accepted-plan view. */
function buildMonthlyAllocatedHours(lines: DemandLine[]): Record<string, number> {
  const allocated: Record<string, number> = {};
  for (const line of lines) {
    if (!line.month) continue;
    const hours = Math.max(0, line.allocatedHours ?? 0);
    if (hours <= 0) continue;
    allocated[line.month] = hours;
  }
  return allocated;
}

// ── Earliest / latest dates from DemandLines ──────────────────────────────────

/** Normalise a backend date/datetime string to a plain ISO date ('YYYY-MM-DD'). */
function toIsoDate(value: string): string {
  return value.split('T')[0];
}

/**
 * Fallback start date when the demand carries no `requestedStartDate`.
 *
 * The Demand model exposes no other desired-start field, so the earliest
 * demand line's `monthStart` (the 1st of that month) is the only available
 * signal — the request is genuinely for "somewhere in that month". When a real
 * `requestedStartDate` exists it always wins (see demandToResourceRequest) and
 * the planner clamps every proposed slot to it.
 */
function deriveStartDate(lines: DemandLine[]): string {
  if (lines.length === 0) return new Date().toISOString().split('T')[0];
  const sorted = [...lines].sort((a, b) => a.monthStart.localeCompare(b.monthStart));
  return toIsoDate(sorted[0].monthStart);
}

function deriveEndDate(lines: DemandLine[]): string | undefined {
  if (lines.length === 0) return undefined;
  const sorted = [...lines].sort((a, b) => b.monthEnd.localeCompare(a.monthEnd));
  return toIsoDate(sorted[0].monthEnd);
}

// ── Skills parsing ────────────────────────────────────────────────────────────

/**
 * Parse the skills JSON string into skill names paired with how strongly they
 * are wanted. Handles both plain string arrays and object arrays with
 * PascalCase (C# serialised) or camelCase keys.
 *
 * A skill with no recognisable `requirement` is `preferred`: the source system
 * only started sending the distinction recently, and treating an unspecified
 * skill as required would harden a signal the requester never asked for.
 */
export function parseSkills(raw: string): RequestedSkill[] {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((s: unknown): RequestedSkill => {
      if (typeof s === 'string') return { name: s, requirement: 'preferred' };
      if (s && typeof s === 'object') {
        const obj = s as Record<string, unknown>;
        return {
          name: String(obj.SkillName ?? obj.skillName ?? obj.Name ?? obj.name ?? ''),
          requirement: parseRequirement(obj.Requirement ?? obj.requirement),
        };
      }
      return { name: '', requirement: 'preferred' };
    }).filter(s => s.name !== '');
  } catch {
    return [];
  }
}

function parseRequirement(raw: unknown): SkillRequirement {
  return String(raw ?? '').trim().toLowerCase() === 'required' ? 'required' : 'preferred';
}

// ── Main adapter ──────────────────────────────────────────────────────────────

/** Hours still to allocate on a demand, never negative. */
function demandRemainingHours(demand: Demand): number {
  const remaining = typeof demand.remainingHours === 'number'
    ? demand.remainingHours
    : demand.totalHours - (demand.allocatedHours ?? 0);
  return Math.max(0, remaining);
}

export function demandToResourceRequest(demand: Demand): ResourceRequest {
  const lines = demand.demandLines ?? [];
  const skills = demand.skills ? parseSkills(demand.skills) : [];
  const hints = buildMonthlyHourHints(lines);
  const allocated = buildMonthlyAllocatedHours(lines);
  const allocatedHours = Math.max(0, demand.allocatedHours ?? 0);

  return {
    id:                   String(demand.id),
    projectId:            demand.projectId,
    projectName:          demand.projectName,
    projectCode:          demand.projectCode || undefined,
    requestedByName:      demand.requestedByName || demand.source || 'Unknown',
    requestedBySystem:    demand.source || 'EBProjects',
    role:                 demand.roleName,
    roleCode:             typeof demand.roleId === 'number' ? demand.roleId : undefined,
    skills:               skills.map(s => s.name),
    skillsDetailed:       skills.length > 0 ? skills : undefined,
    totalHoursRequested:  demandRemainingHours(demand),
    allocatedHours,
    originalHoursRequested: demand.totalHours,
    monthlyAllocatedHours: Object.keys(allocated).length > 0 ? allocated : undefined,
    requestedStartDate:   demand.requestedStartDate
                            ? toIsoDate(demand.requestedStartDate)
                            : deriveStartDate(lines),
    requestedEndDate:     demand.requestedEndDate
                            ? toIsoDate(demand.requestedEndDate)
                            : deriveEndDate(lines),
    status:               mapStatus(demand),
    priority:             mapPriority(demand.priority),
    receivedAt:           demand.createdAt,
    notes:                demand.notes || undefined,
    monthlyHourHints:     Object.keys(hints).length > 0 ? hints : undefined,
    location:             demand.location || undefined,
  };
}

/**
 * Convert an array of Demands to ResourceRequests.
 */
export function demandsToResourceRequests(demands: Demand[]): ResourceRequest[] {
  return demands.map(demandToResourceRequest);
}

/**
 * Find the DemandLine that corresponds to a given month key ('YYYY-MM').
 * Returns undefined if no matching line exists.
 */
export function findDemandLineByMonth(
  lines: DemandLine[],
  monthKey: string,
): DemandLine | undefined {
  return lines.find(l => l.month === monthKey);
}
